/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Type-Safe Hono RPC API Client (Task 10.1.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { hc } from 'hono/client';
import type { AppType } from '@sofia/api-worker';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { useAuthStore } from '../stores/useAuthStore';
import { getWorkspaceDomains } from './urls';

/**
 * Determine the API base origin based on environment or window location.
 * In development, Vite proxies `/api` to local worker `:8787`.
 * In production, requests dispatch directly to the edge worker apiOrigin.
 */
export function getApiBaseUrl(): string {
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  return getWorkspaceDomains().apiOrigin;
}

/**
 * Type-safe Hono RPC Client with dynamic Authorization and Workspace Context headers.
 */
export const client = hc<AppType>(getApiBaseUrl(), {
  headers: () => {
    const headers: Record<string, string> = {};
    const workspaceId = useWorkspaceStore.getState().activeWorkspaceId;
    const token = useAuthStore.getState().accessToken;

    if (workspaceId) {
      headers['x-workspace-id'] = workspaceId;
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  },
});

export type ApiClient = typeof client;
